/**
 * Interview Q&A Page
 *
 * A practice library of behavioral interview questions (shared across all
 * users) plus the user's own private stars, answers, and AI feedback. Built
 * to double as a quick-recall cheat sheet during an interview: a prominent
 * central search bar filters questions, answers, and tags instantly, and
 * starred questions can be pulled up in one click.
 *
 * Unlike Application Q&A (polished copy/paste answers), each entry here holds
 * SPEAKING NOTES: flashcard-style bullets the user glances at mid-interview.
 * The user authors every note; AI feedback (Active accounts only) is
 * review-only and never rewrites — it coaches on STAR coverage, a concrete
 * Result, story coherence, and flashcard brevity (never grammar/spelling).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Copy,
  Trash2,
  Sparkles,
  Loader2,
  X,
  ChevronDown,
  ChevronRight,
  Search,
  Star,
  Briefcase,
} from 'lucide-react';
import { api } from '../services/api';
import type { InterviewQuestion, InterviewUserData, UserJob } from '../types';

const EMPTY_USER_DATA: InterviewUserData = { starred: false, answer: '', recommendations: '' };
const MY_QUESTIONS_TAG = 'My Questions';

type SortMode = 'az' | 'recent' | 'starred';

/** Order tags for the filter dropdown: "My Questions" first, then non-AWS
 *  tags alpha-sorted, then AWS-* tags alpha-sorted. */
function orderTags(tags: [string, number][]): [string, number][] {
  const mine = tags.filter(([t]) => t === MY_QUESTIONS_TAG);
  const nonAws = tags
    .filter(([t]) => t !== MY_QUESTIONS_TAG && !t.startsWith('AWS-'))
    .sort((a, b) => a[0].localeCompare(b[0]));
  const aws = tags
    .filter(([t]) => t.startsWith('AWS-'))
    .sort((a, b) => a[0].localeCompare(b[0]));
  return [...mine, ...nonAws, ...aws];
}

export function InterviewQA() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [questions, setQuestions] = useState<InterviewQuestion[]>([]);
  const [userData, setUserData] = useState<Record<string, InterviewUserData>>({});
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [starredOnly, setStarredOnly] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('az');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [composerOpen, setComposerOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // "Interviewing for" filter: jobs the user has in the Interviewing phase, plus
  // the qids matched to the currently-selected job (fetched on demand). The
  // selection is deep-linkable via ?job=<jobid> (the job card's "Practice
  // Interview Questions" button opens the page pre-filtered to itself).
  const [interviewJobs, setInterviewJobs] = useState<UserJob[]>([]);
  const [interviewingJobId, setInterviewingJobId] = useState<string | null>(
    searchParams.get('job'),
  );
  const [matchedQids, setMatchedQids] = useState<Set<string> | null>(null);
  // Matching runs async server-side (it can take a few seconds); the page polls
  // getJob until the result is ready. 'matching' = still computing (show a
  // loader, DON'T show the unfiltered list); 'ready' = matchedQids is authoritative.
  const [matchStatus, setMatchStatus] = useState<'idle' | 'matching' | 'ready' | 'error'>('idle');

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.listInterviewQA();
        setQuestions(resp.questions || []);
        setUserData(resp.user_data || {});
        setActive(!!resp.active);
      } catch (err) {
        console.error('Failed to load interview questions', err);
        setErrorMsg('Could not load the interview questions. Try again in a moment.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load the user's Interviewing-phase job cards to populate the filter.
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.listJobs('Interviewing');
        setInterviewJobs(resp.jobs || []);
      } catch (err) {
        console.error('Failed to load interviewing jobs', err);
      }
    })();
  }, []);

  // When a job is selected (via the dropdown or the ?job deep-link), fetch its
  // matched question ids. getJob() also triggers a server-side stale refresh, so
  // questions added since the card entered Interviewing get picked up.
  useEffect(() => {
    if (!interviewingJobId) {
      setMatchedQids(null);
      setMatchStatus('idle');
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    setMatchedQids(null);
    setMatchStatus('matching');

    const poll = async () => {
      try {
        const job = await api.getJob(interviewingJobId);
        if (cancelled) return;
        // 'matching' means the async compute is still running → keep polling.
        // Any other state (done, or legacy/absent) is terminal: use whatever
        // matches are stored (possibly none). This avoids re-triggering matching
        // on every poll if the compute finished with zero/failed.
        const stillMatching = job.interview_match_state === 'matching';
        if (!stillMatching || tries >= 20 /* ~60s ceiling */) {
          setMatchedQids(new Set((job.interview_matches || []).map((m) => m.qid)));
          setMatchStatus('ready');
          return;
        }
        tries += 1;
        timer = setTimeout(poll, 3000);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load job question matches', err);
        setMatchStatus('error');
        setMatchedQids(new Set());
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [interviewingJobId]);

  const selectInterviewingJob = (jobid: string | null) => {
    setInterviewingJobId(jobid);
    const next = new URLSearchParams(searchParams);
    if (jobid) next.set('job', jobid);
    else next.delete('job');
    setSearchParams(next, { replace: true });
  };

  const selectedJob = interviewJobs.find((j) => j.jobid === interviewingJobId) || null;

  const dataFor = (qid: string): InterviewUserData => userData[qid] || EMPTY_USER_DATA;

  const setDataFor = (qid: string, patch: Partial<InterviewUserData>) => {
    setUserData((prev) => ({ ...prev, [qid]: { ...(prev[qid] || EMPTY_USER_DATA), ...patch } }));
  };

  const toggleExpanded = (qid: string) => {
    setExpanded((prev) => ({ ...prev, [qid]: !prev[qid] }));
  };

  // All tags across visible questions, ordered for the filter dropdown:
  // "My Questions" pinned first, then non-AWS alpha, then AWS-* alpha.
  const allTags = useMemo(() => {
    const counts = new Map<string, number>();
    questions.forEach((q) => (q.tags || []).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)));
    return orderTags([...counts.entries()]);
  }, [questions]);

  const toggleTagSelected = (tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  // The central quick search: question text + the user's answer + tags.
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = questions;
    // "Interviewing for" AND "Starred" combine as AND (both narrow the list).
    if (interviewingJobId && matchedQids) {
      list = list.filter((it) => matchedQids.has(it.qid));
    }
    if (starredOnly) list = list.filter((it) => dataFor(it.qid).starred);
    if (selectedTags.length > 0) {
      // Multi-select is an OR filter: show questions carrying ANY selected tag
      list = list.filter((it) => (it.tags || []).some((t) => selectedTags.includes(t)));
    }
    if (q) {
      list = list.filter((it) => {
        const d = dataFor(it.qid);
        const haystack = `${it.question}\n${d.answer}\n${(it.tags || []).join('\n')}`.toLowerCase();
        return haystack.includes(q);
      });
    }
    const sorted = [...list];
    if (sortMode === 'az') {
      sorted.sort((a, b) => a.question.localeCompare(b.question));
    } else if (sortMode === 'recent') {
      sorted.sort((a, b) => (dataFor(b.qid).updated_at || '').localeCompare(dataFor(a.qid).updated_at || ''));
    } else {
      // starred first, then answered, then A–Z — the cheat-sheet ordering
      const rank = (it: InterviewQuestion) => {
        const d = dataFor(it.qid);
        return (d.starred ? 0 : 2) + (d.answer.trim() ? 0 : 1);
      };
      sorted.sort((a, b) => rank(a) - rank(b) || a.question.localeCompare(b.question));
    }
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, userData, searchQuery, selectedTags, starredOnly, sortMode, interviewingJobId, matchedQids]);

  const starredCount = useMemo(
    () => questions.filter((it) => dataFor(it.qid).starred).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, userData],
  );

  const handleToggleStar = async (qid: string) => {
    const next = !dataFor(qid).starred;
    setDataFor(qid, { starred: next }); // optimistic — stars must feel instant
    try {
      const updated = await api.updateInterviewAnswer(qid, { starred: next });
      setDataFor(qid, updated);
    } catch (err) {
      console.error('Failed to toggle star', err);
      setDataFor(qid, { starred: !next }); // roll back
    }
  };

  const handleSaveAnswer = async (qid: string, answer: string) => {
    try {
      setBusyId(qid);
      const updated = await api.updateInterviewAnswer(qid, { answer });
      setDataFor(qid, updated);
    } catch (err) {
      console.error('Failed to save answer', err);
      alert('Could not save your answer.');
    } finally {
      setBusyId(null);
    }
  };

  const handleFeedback = async (qid: string, answer: string) => {
    if (!active) return; // server enforces too
    try {
      setFeedbackId(qid);
      await api.updateInterviewAnswer(qid, { answer }); // persist unsaved edits first
      const updated = await api.getInterviewFeedback(qid);
      setDataFor(qid, updated);
    } catch (err: any) {
      console.error('Failed to get feedback', err);
      const msg = err?.response?.data?.message || 'Could not generate feedback right now.';
      alert(msg);
    } finally {
      setFeedbackId(null);
    }
  };

  const handleAddQuestion = async () => {
    const text = newQuestion.trim();
    if (!text) return;
    try {
      setAddBusy(true);
      const created = await api.createInterviewQuestion(text);
      setQuestions((prev) => [created, ...prev]);
      setExpanded((prev) => ({ ...prev, [created.qid]: true }));
      setNewQuestion('');
      setComposerOpen(false);
    } catch (err: any) {
      console.error('Failed to add question', err);
      alert(err?.response?.data?.message || 'Could not add your question.');
    } finally {
      setAddBusy(false);
    }
  };

  const handleDeleteQuestion = async (qid: string) => {
    if (!confirm('Delete this question (and your answer to it)? This cannot be undone.')) return;
    try {
      setBusyId(qid);
      await api.deleteInterviewQuestion(qid);
      setQuestions((prev) => prev.filter((it) => it.qid !== qid));
    } catch (err) {
      console.error('Failed to delete question', err);
      alert('Could not delete this question.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header — minimal, with a clear path back to the main app */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <a href="/" className="flex items-center space-x-2">
              <img src="/assets/skillsnap_logo.png" alt="SkillSnap" className="h-8 w-auto" />
              <span className="text-xl font-semibold text-primary-600">SkillSnap</span>
            </a>
            <Link
              to="/Ready"
              data-help-target="interview.backtojobs"
              className="flex items-center space-x-1.5 px-3 py-1.5 text-sm text-gray-700 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Jobs</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page intro */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Interview Q&amp;A</h1>
          <p className="text-sm text-gray-700 leading-relaxed">
            Practice the questions you're most likely to be asked. Star the ones you want
            to nail, jot your speaking notes, and pull any of them up in seconds with the
            search bar — even mid-interview.
          </p>
          <p className="text-sm text-gray-700 leading-relaxed mt-2">
            Why two Q&amp;A pages? <strong>Interview Q&amp;A</strong> (this page) holds{' '}
            <strong>speaking notes</strong> — flashcard-style bullets that prompt you
            during a live interview, not text to read verbatim;{' '}
            <Link to="/application-qa" className="text-primary-600 hover:text-primary-700 underline">
              Application Q&amp;A
            </Link>{' '}
            holds polished, full-sentence answers ready to copy/paste into application
            forms.
          </p>
        </div>

        {/* Central quick-search — the primary control on this page */}
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-help-target="interview.search"
              data-uat="interview-search"
              placeholder="Quick search questions, notes, and tags…"
              className="w-full pl-11 pr-10 py-3 border border-gray-300 rounded-xl text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Search interview questions and notes"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filters / sort / add */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setStarredOnly((v) => !v)}
            data-uat="interview-starred-filter"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              starredOnly
                ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title="Show only starred questions"
          >
            <Star className={`w-4 h-4 ${starredOnly ? 'fill-yellow-400 text-yellow-500' : ''}`} />
            <span>Starred{starredCount > 0 ? ` (${starredCount})` : ''}</span>
          </button>

          {/* "Interviewing for" filter: narrow to the questions matched to a job
              you're actively interviewing for. Combines with Starred as AND. */}
          {interviewJobs.length > 0 && (
            <div className={`flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg text-sm border transition-colors ${
              interviewingJobId
                ? 'bg-pink-50 border-pink-300 text-pink-800'
                : 'bg-white border-gray-300 text-gray-700'
            }`}>
              <Briefcase className="w-4 h-4 shrink-0" />
              <select
                value={interviewingJobId || ''}
                onChange={(e) => selectInterviewingJob(e.target.value || null)}
                data-uat="interview-job-filter"
                className="bg-transparent max-w-56 truncate text-sm focus:outline-none cursor-pointer"
                title="Show only questions matched to a job you're interviewing for"
              >
                <option value="">Interviewing for: any</option>
                {interviewJobs.map((j) => (
                  <option key={j.jobid} value={j.jobid}>
                    {j.jobtitle}{j.jobcompany ? ` — ${j.jobcompany}` : ''}
                  </option>
                ))}
              </select>
              {matchStatus === 'matching' && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />}
            </div>
          )}

          {/* Multi-select tag filter (OR semantics across checked tags) */}
          <div
            className="relative"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setTagPickerOpen(false);
            }}
          >
            <button
              type="button"
              onClick={() => setTagPickerOpen((v) => !v)}
              data-uat="interview-tag-filter"
              aria-haspopup="listbox"
              aria-expanded={tagPickerOpen}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors max-w-64 ${
                selectedTags.length > 0
                  ? 'bg-primary-50 border-primary-300 text-primary-800'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title="Filter by one or more tags"
            >
              <span className="truncate">
                {selectedTags.length === 0
                  ? 'All Tags'
                  : selectedTags.length === 1
                    ? selectedTags[0]
                    : `${selectedTags.length} tags`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 shrink-0" />
            </button>
            {tagPickerOpen && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-72 max-h-80 flex flex-col">
                <button
                  type="button"
                  onClick={() => setSelectedTags([])}
                  data-uat="interview-tag-clear"
                  className={`text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-gray-50 ${
                    selectedTags.length === 0 ? 'text-primary-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  All Tags{selectedTags.length > 0 ? ' (clear selection)' : ''}
                </button>
                <div className="overflow-y-auto flex-1 py-1">
                  {allTags.map(([tag, count], idx) => {
                    const checked = selectedTags.includes(tag);
                    // Divider before the first AWS-* tag to visually separate the groups
                    const firstAws =
                      tag.startsWith('AWS-') && (idx === 0 || !allTags[idx - 1][0].startsWith('AWS-'));
                    return (
                      <div key={tag}>
                        {firstAws && <hr className="my-1 border-gray-100" />}
                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTagSelected(tag)}
                          />
                          <span className={`flex-1 truncate ${tag.startsWith('AWS-') ? 'text-orange-700' : 'text-gray-800'}`}>
                            {tag}
                          </span>
                          <span className="text-xs text-gray-400">{count}</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as SortMode)}
            data-uat="interview-sort"
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Sort questions"
          >
            <option value="az">Sort: A–Z</option>
            <option value="starred">Sort: Starred first</option>
            <option value="recent">Sort: Recently updated</option>
          </select>

          <div className="flex-1" />
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {interviewingJobId && matchStatus === 'matching'
              ? 'matching…'
              : `${filtered.length} of ${questions.length}`}
          </span>
          <button
            onClick={() => setComposerOpen((v) => !v)}
            data-help-target="interview.addquestion"
            data-uat="interview-add-question"
            className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            title="Add a question of your own"
          >
            <Plus className="w-4 h-4" />
            <span>Add Question</span>
          </button>
        </div>

        {/* New-question composer */}
        {composerOpen && (
          <div className="mb-4 bg-white border border-primary-200 rounded-lg p-4 shadow-sm">
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Your new question
            </label>
            <textarea
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              rows={2}
              maxLength={500}
              autoFocus
              data-uat="interview-new-question"
              placeholder="e.g. Tell me about a time you had to win back a frustrated stakeholder."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Your question appears immediately in your list (tagged &ldquo;{MY_QUESTIONS_TAG}&rdquo;).
              It's also submitted for review — if it's new and appropriate it may be added to the
              shared library for everyone.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={handleAddQuestion}
                disabled={addBusy || !newQuestion.trim()}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  newQuestion.trim() && !addBusy
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {addBusy ? 'Adding…' : 'Add'}
              </button>
              <button
                onClick={() => { setComposerOpen(false); setNewQuestion(''); }}
                className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Active "interviewing for" context banner */}
        {interviewingJobId && (
          <div className="mb-4 flex items-center gap-2 bg-pink-50 border border-pink-200 rounded-lg px-4 py-2.5 text-sm text-pink-900">
            <Briefcase className="w-4 h-4 shrink-0" />
            <span className="flex-1 min-w-0">
              {matchStatus === 'matching' ? (
                <>Matching questions to your interview
                  {selectedJob ? (
                    <> for <strong>{selectedJob.jobtitle}</strong>
                      {selectedJob.jobcompany ? <> at <strong>{selectedJob.jobcompany}</strong></> : null}</>
                  ) : null}…</>
              ) : (
                <>Showing questions matched to your interview
                  {selectedJob ? (
                    <> for <strong>{selectedJob.jobtitle}</strong>
                      {selectedJob.jobcompany ? <> at <strong>{selectedJob.jobcompany}</strong></> : null}</>
                  ) : null}
                  {matchStatus === 'ready' && matchedQids ? (
                    <span className="text-pink-700"> ({matchedQids.size} matched)</span>
                  ) : null}.</>
              )}
            </span>
            <button
              onClick={() => selectInterviewingJob(null)}
              data-uat="interview-job-clear"
              className="flex items-center gap-1 px-2 py-1 rounded text-pink-700 hover:bg-pink-100 transition-colors shrink-0"
              title="Show all questions"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : interviewingJobId && matchStatus === 'matching' ? (
          // Matching is async — show a loader, NOT the full unfiltered list (which
          // would misleadingly read as "no filtering happened").
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-600" data-uat="interview-matching">
            <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto mb-3" />
            <p className="text-sm font-medium">Matching interview questions to this job…</p>
            <p className="text-xs text-gray-500 mt-1">
              Scoring the question library against the job's skills — this takes a few seconds.
            </p>
          </div>
        ) : interviewingJobId && matchStatus === 'error' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center text-amber-800">
            <p className="text-sm">
              Couldn't load the matched questions.{' '}
              <button className="underline" onClick={() => selectInterviewingJob(interviewingJobId)}>Retry</button>{' '}or{' '}
              <button className="underline" onClick={() => selectInterviewingJob(null)}>show all questions</button>.
            </p>
          </div>
        ) : errorMsg ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
            {errorMsg}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            <p className="text-sm">
              {questions.length === 0
                ? 'No questions yet.'
                : interviewingJobId && matchedQids && matchedQids.size === 0
                  ? "No practice questions matched this job's skills. Clear the filter to browse the full library."
                  : starredOnly && !searchQuery
                    ? 'No starred questions yet — click the star on any question to pin it here.'
                    : 'Nothing matches your filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((q, i) => (
              <QuestionCard
                key={q.qid}
                helpTarget={i === 0 ? 'interview.question0' : undefined}
                question={q}
                data={dataFor(q.qid)}
                isMine={q.userid !== 'public'}
                active={active}
                isBusy={busyId === q.qid}
                isFeedbackBusy={feedbackId === q.qid}
                isExpanded={!!expanded[q.qid]}
                onToggleExpanded={() => toggleExpanded(q.qid)}
                onToggleStar={() => handleToggleStar(q.qid)}
                onSave={(answer) => handleSaveAnswer(q.qid, answer)}
                onFeedback={(answer) => handleFeedback(q.qid, answer)}
                onDelete={() => handleDeleteQuestion(q.qid)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer links match the rest of the app */}
      <footer className="border-t border-gray-200 bg-white py-4">
        <div className="flex justify-center gap-4 text-xs text-gray-500">
          <a href="/docs/toc.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary-600 transition-colors">
            Terms
          </a>
          <a href="/docs/pp.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary-600 transition-colors">
            Privacy
          </a>
        </div>
      </footer>
    </div>
  );
}

interface QuestionCardProps {
  question: InterviewQuestion;
  data: InterviewUserData;
  helpTarget?: string;
  isMine: boolean;
  active: boolean;
  isBusy: boolean;
  isFeedbackBusy: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onToggleStar: () => void;
  onSave: (answer: string) => void;
  onFeedback: (answer: string) => void;
  onDelete: () => void;
}

function QuestionCard({
  question,
  data,
  helpTarget,
  isMine,
  active,
  isBusy,
  isFeedbackBusy,
  isExpanded,
  onToggleExpanded,
  onToggleStar,
  onSave,
  onFeedback,
  onDelete,
}: QuestionCardProps) {
  const [answerText, setAnswerText] = useState(data.answer);

  // Re-sync when the parent updates this row (e.g. after Get Feedback)
  useEffect(() => {
    setAnswerText(data.answer);
  }, [data.answer]);

  const dirty = answerText !== data.answer;
  const hasAnswer = data.answer.trim().length > 0;
  const hasRecs = data.recommendations.trim().length > 0;

  return (
    <div
      data-help-target={helpTarget}
      data-uat="interview-question-card"
      className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
    >
      {/* Header row: star + question + badges. Click toggles the accordion. */}
      <div className="flex items-start gap-1.5 pl-2 pr-4 py-3">
        <button
          type="button"
          onClick={onToggleStar}
          data-uat="interview-star"
          aria-label={data.starred ? 'Unstar this question' : 'Star this question'}
          aria-pressed={data.starred}
          className="p-1.5 mt-0.5 rounded-lg hover:bg-yellow-50 transition-colors shrink-0"
          title={data.starred ? 'Unstar' : 'Star to pin this question to your shortlist'}
        >
          <Star
            className={`w-5 h-5 ${
              data.starred ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300 hover:text-yellow-400'
            }`}
          />
        </button>

        {/* Accordion toggle: password managers get confused by Q&A-labelled
            buttons, hence the suppression attributes (see LEARNINGS). */}
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={isExpanded}
          data-lpignore="true"
          data-form-type="other"
          data-1p-ignore="true"
          data-uat="interview-toggle"
          className="flex-1 min-w-0 flex items-start gap-2 text-left group"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500 shrink-0 mt-1" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500 shrink-0 mt-1" />
          )}
          <span className="flex-1 min-w-0">
            {/* Question text stays large and readable — this doubles as a cheat sheet */}
            <span className="block text-base font-medium text-gray-900 leading-snug group-hover:text-primary-700 transition-colors">
              {question.question}
            </span>
            {(question.tags || []).length > 0 && (
              <span className="mt-1 flex flex-wrap gap-1">
                {(question.tags || []).map((t) => (
                  <span
                    key={t}
                    className={`px-1.5 py-0.5 rounded text-[11px] border ${
                      t === MY_QUESTIONS_TAG
                        ? 'bg-purple-50 text-purple-700 border-purple-200'
                        : t.startsWith('AWS-')
                          ? 'bg-orange-50 text-orange-700 border-orange-200'
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                    }`}
                  >
                    {t}
                  </span>
                ))}
              </span>
            )}
          </span>
          <span className="hidden sm:flex items-center gap-1 text-xs shrink-0 mt-0.5">
            {hasAnswer && (
              <span className="px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded">
                notes
              </span>
            )}
            {hasRecs && (
              <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded">
                feedback
              </span>
            )}
          </span>
        </button>
      </div>

      {/* Accordion body: the Answer */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 sm:pl-14">
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs uppercase tracking-wide text-gray-500">
                Your Speaking Notes{' '}
                <span className="normal-case">(flashcard bullets to glance at — not a script)</span>
              </label>
              {answerText.trim() && (
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(answerText); }}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-xs text-gray-400 hover:text-primary-600 rounded hover:bg-gray-100 transition-colors"
                  title="Copy notes to clipboard"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              )}
            </div>
            {/* Large, relaxed type: readable at a glance mid-interview */}
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              rows={Math.min(14, Math.max(5, answerText.split('\n').length + 2))}
              data-uat="interview-answer"
              placeholder={"Major bullets that prompt you in real time, e.g.\n• Situation: one line of context (enough to still make sense next year)\n• Tactic / approach\n• Action: what YOU did\n• Result: the concrete outcome"}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {hasRecs && (
            <div className="mb-3">
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                AI Feedback <span className="normal-case">(review-only — your answer is never changed)</span>
              </label>
              <div className="px-3 py-2 border border-amber-200 bg-amber-50/40 rounded-lg text-sm text-gray-800 whitespace-pre-wrap">
                {data.recommendations}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={() => onSave(answerText)}
              disabled={!dirty || isBusy}
              data-uat="interview-save"
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                dirty && !isBusy
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
              title={dirty ? 'Save your answer' : 'No unsaved changes'}
            >
              {isBusy ? 'Saving…' : 'Save'}
            </button>

            {/* Wrapper carries the hover state: disabled buttons swallow mouse
                events, so the tooltip (non-Active users only) hangs off this
                span. 0.5s reveal delay via group-hover:delay-500. */}
            <span className="relative group inline-flex">
              <button
                onClick={() => onFeedback(answerText)}
                disabled={!active || isFeedbackBusy || !answerText.trim()}
                data-uat="interview-feedback"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  !active || !answerText.trim()
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-amber-500 text-white hover:bg-amber-600'
                }`}
                title={
                  !active
                    ? undefined // custom delayed tooltip below
                    : !answerText.trim()
                      ? 'Jot some notes first'
                      : 'AI review of your notes — STAR coverage, a concrete Result, flashcard brevity (no rewriting)'
                }
              >
                {isFeedbackBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                <span>{isFeedbackBusy ? 'Reviewing…' : 'Get Feedback'}</span>
              </button>
              {!active && (
                <span
                  role="tooltip"
                  data-uat="interview-feedback-tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg bg-gray-900 text-white text-xs leading-relaxed px-3 py-2 shadow-lg z-50 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-hover:delay-500"
                >
                  AI feedback on your answers is available on{' '}
                  <strong className="font-semibold">Active</strong> accounts. You can
                  still browse, star, and write answers.
                  <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </span>
              )}
            </span>

            <div className="flex-1" />

            {isMine && (
              <button
                onClick={onDelete}
                disabled={isBusy}
                data-uat="interview-delete"
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Delete this question of yours"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
